/**
 * @typedef {Object} CustomProps
 * @property {React.ReactNode}  [helperText]  to display the custom helper text below the input box
 */

import React, { useEffect, useState } from 'react';

import cx from 'classnames';
import { isEmpty } from 'lodash';

import themeConfig from '@/theme/themeConfig';
import { SearchIcon } from '@/assets/icons';

import styles from './InlineSearchSelect.module.scss';

import { Checkbox, Input } from '.';

/**
 * @description UI Component to display the select component inline.
 *
 * @author  Akshay Jain <@akshay.jain2>
 *
 * @param { CustomProps } props https://ant.design/components/input
 *
 * @returns {React.Component}
 */

const InlineSearchSelect = (props) => {
	const {
		options = [],
		selectedOptions = [],
		selectedFilterType,
		isMultiSelect = true,
		allAppliedFilters = {},
		setSelectedOptions,
		showSelectAll = true,
		allOrSingle: isAllOrSingle = false,
		isSelectAllDisabled = () => false,
		classes = {},
		inputProps: parentInputProps = {},
		allowCustomOption = false,
	} = props;

	const [isInputFocused, setIsInputFocused] = useState(false);
	const [searchInput, setSearchInput] = useState('');
	const [optionsToShow, setOptionsToShow] = useState(options);
	const [isAllOptionsSelected, setIsAllOptionsSelected] = useState(false);

	useEffect(() => {
		setIsAllOptionsSelected(
			selectedOptions.length === optionsToShow.length &&
				selectedOptions.length !== 0,
		);
	}, [optionsToShow, selectedOptions]);

	useEffect(() => {
		if (isEmpty(selectedOptions)) return;

		const allCustomOptions =
			selectedOptions.filter((val) => val.isCustomOption) || [];
		setOptionsToShow([...allCustomOptions, ...options]);
	}, [options, selectedOptions]);

	const handleInputFocus = () => {
		setIsInputFocused(true);
	};

	const handleInputBlur = () => {
		setIsInputFocused(false);
	};

	const handleInputChange = (ev) => {
		setSearchInput(ev.target.value);
	};

	const handleSelectAllClick = () => {
		if (isSelectAllDisabled(allAppliedFilters, selectedFilterType)) return;

		if (isAllOptionsSelected) {
			setSelectedOptions([]);
		} else {
			const _optionValues = optionsToShow;
			setSelectedOptions(_optionValues);
		}
	};

	const handleOptionClick = (selectedOption) => {
		let _updatedSelectedOptions = [...selectedOptions];
		const _currentOptionIndex = _updatedSelectedOptions
			?.map((opt) => opt.value)
			?.indexOf(selectedOption.value);

		if (
			_currentOptionIndex === -1 &&
			selectedOption.isDisabled?.(allAppliedFilters)
		)
			return;

		if (!isMultiSelect) {
			_updatedSelectedOptions =
				_currentOptionIndex === -1 ? [selectedOption] : [];
		} else if (isAllOrSingle) {
			_updatedSelectedOptions = [selectedOption];
		} else {
			if (_currentOptionIndex > -1) {
				_updatedSelectedOptions?.splice(_currentOptionIndex, 1);
			} else {
				_updatedSelectedOptions?.push(selectedOption);
			}
		}
		setSelectedOptions(_updatedSelectedOptions);
	};

	const renderListWithSearch = () => {
		const _searchInput = searchInput?.toLowerCase();
		const optionsRenderer = optionsToShow
			.map((optionItem) => {
				const _optionSearchString =
					`${optionItem.title} ${optionItem.subtitle}`.toLowerCase();
				const isSelected = selectedOptions.find(
					(opt) => opt.value === optionItem.value,
				);

				if (
					!_searchInput ||
					(_searchInput && _optionSearchString.includes(_searchInput))
				) {
					return (
						<Checkbox
							key={optionItem.key}
							className={cx(styles.listItem, {
								[styles.active]: isSelected,
							})}
							onChange={() => handleOptionClick(optionItem)}
							disabled={
								!isSelected && optionItem.isDisabled?.(allAppliedFilters)
							}
							checked={isSelected}
							value={optionItem.value}
						>
							{optionItem.title}
							{!isEmpty(optionItem.subtitle) && (
								<span className={styles.subtitle}>({optionItem.subtitle})</span>
							)}
						</Checkbox>
					);
				}
			})
			.filter((opt) => opt);

		if (isEmpty(optionsRenderer)) {
			if (allowCustomOption && !isEmpty(_searchInput)) {
				const customOption = {
					key: `${_searchInput}`,
					subtitle: `${_searchInput}`,
					title: `${_searchInput}`,
					value: `${_searchInput}`,
					isCustomOption: true,
				};
				const isSelected = selectedOptions.find(
					(opt) => opt.value === customOption.value,
				);
				return (
					<Checkbox
						key={customOption.key}
						className={cx(styles.listItem, {
							[styles.active]: isSelected,
						})}
						onChange={() => handleOptionClick(customOption)}
						disabled={
							!isSelected && customOption.isDisabled?.(allAppliedFilters)
						}
						checked={isSelected}
						value={customOption.value}
					>
						{customOption.title}
						{!isEmpty(customOption.subtitle) && (
							<span className={styles.subtitle}>({customOption.subtitle})</span>
						)}
					</Checkbox>
				);
			}
			return (
				<div className={styles.noResultWrapper}>
					<SearchIcon className={styles.searchIcon} />
					<div className={styles.overflowDiv}>
						{searchInput ? (
							<>
								<div className={styles.noResultHeading}>
									{searchInput} not found.
								</div>
								<div>
									Your search “{searchInput}” did not match any result. Please
									try again.
								</div>
							</>
						) : (
							'No result found.'
						)}
					</div>
				</div>
			);
		}

		return optionsRenderer;
	};

	const inputProps = {
		id: 'inlineSearch',
		placeholder: 'Search by Name or ID',
		allowClear: true,
		disabled: isEmpty(optionsToShow),
		value: searchInput,
		onFocus: handleInputFocus,
		onBlur: handleInputBlur,
		onChange: handleInputChange,
		className: cx(styles.inputContainer, classes.inputContainer),
		prefix: (
			<SearchIcon
				width={22}
				height={22}
				color={
					isInputFocused ? themeConfig.primary : themeConfig.textExtraLight
				}
			/>
		),
		...parentInputProps,
	};
	return (
		<div>
			<Input {...inputProps} />
			<div className={cx(styles.listWrapper, classes.listWrapper)}>
				{showSelectAll &&
					isMultiSelect &&
					!isEmpty(optionsToShow) &&
					isEmpty(searchInput) && (
						<Checkbox
							className={cx(styles.listItem, {
								[styles.active]: isAllOptionsSelected,
								[styles.disabled]: isSelectAllDisabled(
									allAppliedFilters,
									selectedOptions,
								),
							})}
							onChange={handleSelectAllClick}
							checked={isAllOptionsSelected}
							value="all"
						>
							Select All
						</Checkbox>
					)}
				{renderListWithSearch()}
			</div>
		</div>
	);
};

export default InlineSearchSelect;
